import {
  Box,
  Button,
  Chip,
  Divider,
  Paper,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { tokens } from "../../../../../../theme";
import PlayCircleFilledRounded from "@mui/icons-material/PlayCircleFilledRounded";
import KeyboardArrowRightRounded from "@mui/icons-material/KeyboardArrowRightRounded";
import PublicRounded from "@mui/icons-material/PublicRounded";
import DescriptionRounded from "@mui/icons-material/DescriptionRounded";
import LiveTvRounded from "@mui/icons-material/LiveTvRounded"; // Clean addition for empty state icon
import type { StreamData } from "../../../types/types";
import { CalendarIcon, LocationIcon } from "../../../../../../components/icons";
import { useNavigate } from "react-router-dom";

interface StreamSectionProps {
  streamTabs: string[];
  stream?: StreamData; // Marked as optional to handle the empty state safely
  bookingId: string;
  activeTab: number;
  onTabChange: (newIndex: number) => void;
  onViewStream?: () => void;
  onOpenMemorialPage?: () => void;
  onOpenCallsheet?: () => void;
  serviceType?: string;
}

export default function StreamSection({
  streamTabs,
  stream,
  bookingId,
  onViewStream,
  onOpenMemorialPage,
  onOpenCallsheet,
  activeTab,
  onTabChange,
  serviceType = "funeral",
}: StreamSectionProps) {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const navigate = useNavigate();

  const hasStream = Boolean(stream && stream.id);

  const handleAddStreamNavigation = () => {
    navigate(`/stream-management/add`, {
      state: { prefill: { booking: Number(bookingId) } },
    });
  };

  return (
    <Paper sx={{ borderRadius: "12px", p: { xs: 1.5, sm: 2 }, boxShadow: 0, height: "100%" }}>

      {/* Top Header Row */}
      <Box
        sx={{
          overflowX: "auto",
          "&::-webkit-scrollbar": { display: "none" },
          mb: 2,
        }}
      >
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          sx={{ minWidth: "max-content" }}
        >
          {streamTabs.map((tab, index) => {
            const isActive = index === activeTab;
            return (
              <Button
                key={index}
                onClick={() => onTabChange(index)}
                sx={{
                  bgcolor: isActive ? "#724e41" : "rgba(114, 78, 65, 0.08)",
                  color: isActive ? "white" : "#724e41",
                  border: "none",
                  borderRadius: "10px",
                  px: 1.5,
                  py: 0.6,
                  minWidth: "auto",
                  textTransform: "none",
                  fontWeight: isActive ? 700 : 500,
                  fontSize: "12px",
                  whiteSpace: "nowrap",
                  "&:hover": {
                    bgcolor: isActive ? "#5d3e33" : "rgba(114, 78, 65, 0.15)",
                  },
                }}
              >
                {tab}
              </Button>
            );
          })}

          <Button
            onClick={handleAddStreamNavigation}
            sx={{
              bgcolor: "transparent",
              color: "#724e41",
              border: "1px solid #724e41",
              borderRadius: "10px",
              px: 1.5,
              py: 0.6,
              minWidth: "auto",
              textTransform: "none",
              fontWeight: 700,
              fontSize: "12px",
              whiteSpace: "nowrap",
              "&:hover": { bgcolor: "rgba(114, 78, 65, 0.06)" },
            }}
          >
            + Add Stream
          </Button>
        </Stack>
      </Box>

      {/* Card container */}
      <Paper
        sx={{ border: "1px solid #f0f4f8", borderRadius: "14px", p: { xs: 1.5, sm: 2 } }}
        elevation={0}
      >
        {hasStream ? (
          /* PRESENT STATE */
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={2}
            alignItems="stretch"
          >
            {/* LEFT: Stream details */}
            <Box
              sx={{
                flex: 1,
                bgcolor: "background.paper",
                borderRadius: "12px",
                p: { xs: 1.5, sm: 2 },
                position: "relative",
                minHeight: 150,
              }}
            >
              <Chip
                label={`ID:${stream?.id}`}
                sx={{
                  position: "absolute",
                  top: 10,
                  left: 10,
                  bgcolor: colors.primary[900],
                  height: 22,
                  "& .MuiChip-label": {
                    fontSize: "10px",
                    color: "text.secondary",
                    px: 1,
                    fontWeight: 600,
                  },
                }}
              />

              <Typography
                sx={{
                  fontWeight: 800,
                  fontSize: { xs: "16px", sm: "20px" },
                  color: "#4b5563",
                  mt: 4,
                  mb: 2,
                  textTransform: "uppercase",
                }}
              >
                {stream?.title}
              </Typography>

              <Divider sx={{ mb: 2, opacity: 0.6 }} />

              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={{ xs: 2, sm: 4 }}
              >
                {/* Date & Time */}
                <Stack direction="row" spacing={1.2} alignItems="flex-start">
                  <Box>
                    <Typography sx={{ fontSize: 11, color: "text.secondary", ml: 3.5 }}>
                      DATE & TIME
                    </Typography>
                    <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                      <CalendarIcon sx={{ fontSize: 18, color: "text.secondary", mt: "2px" }} />
                      <Typography sx={{ fontWeight: 800, fontSize: 14, color: "#475569" }}>
                        {stream?.date}
                      </Typography>
                    </Box>
                    <Typography sx={{ fontSize: 12, color: "text.secondary", ml: 3.5 }}>
                      {stream?.time}
                    </Typography>
                  </Box>
                </Stack>

                {/* Location */}
                <Stack direction="row" spacing={1.2} alignItems="flex-start">
                  <Box>
                    <Typography sx={{ fontSize: 11, color: "text.secondary", ml: 3.5 }}>
                      LOCATION
                    </Typography>
                    <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                      <LocationIcon sx={{ fontSize: 18, color: "text.secondary", mt: "2px" }} />
                      <Typography
                        sx={{
                          fontWeight: 800,
                          fontSize: 14,
                          color: "#475569",
                          wordBreak: "break-word",
                        }}
                      >
                        {stream?.locationName}
                      </Typography>
                    </Box>
                    <Typography
                      sx={{
                        fontSize: 12,
                        color: "text.secondary",
                        ml: 3.5,
                        wordBreak: "break-word",
                      }}
                    >
                      {stream?.locationAddress}
                    </Typography>
                  </Box>
                </Stack>
              </Stack>
            </Box>

            {/* RIGHT: Action buttons */}
            <Stack
              spacing={1.5}
              sx={{ width: { xs: "100%", md: 320 } }}
            >
              {[
                {
                  label: "View Stream",
                  icon: <PlayCircleFilledRounded sx={{ color: "#475569" }} />,
                  onClick: onViewStream,
                },
                {
                  label: serviceType.toLowerCase() === "funeral" ? "Memorial page" : "Stream page",
                  icon: <PublicRounded sx={{ color: "#475569" }} />,
                  onClick: onOpenMemorialPage,
                },
                {
                  label: "Callsheet",
                  icon: <DescriptionRounded sx={{ color: "#475569" }} />,
                  onClick: onOpenCallsheet,
                },
              ].map(({ label, icon, onClick }) => (
                <Button
                  key={label}
                  onClick={onClick}
                  fullWidth
                  sx={{
                    bgcolor: "action.hover",
                    borderRadius: "14px",
                    p: 2,
                    textTransform: "none",
                    justifyContent: "space-between",
                    "&:hover": {
                      bgcolor: "#724e41",
                      "& .MuiTypography-root": { color: "white" },
                      "& .MuiSvgIcon-root": { color: "#475569" },
                    },
                  }}
                >
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <Box
                      sx={{
                        width: 36,
                        height: 36,
                        bgcolor: colors.primary[900],
                        borderRadius: "10px",
                        display: "grid",
                        placeItems: "center",
                        flexShrink: 0,
                      }}
                    >
                      {icon}
                    </Box>
                    <Typography sx={{ fontWeight: 700, color: "#475569" }}>
                      {label}
                    </Typography>
                  </Stack>
                  <KeyboardArrowRightRounded sx={{ color: "text.secondary" }} />
                </Button>
              ))}
            </Stack>
          </Stack>
        ) : (
          /* INTENTIONAL EMPTY STATE */
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              py: 5,
              px: 2,
              textAlign: "center",
            }}
          >
            <Box
              sx={{
                width: 50,
                height: 50,
                borderRadius: "50%",
                bgcolor: "rgba(114, 78, 65, 0.08)",
                display: "grid",
                placeItems: "center",
                mb: 2,
              }}
            >
              <LiveTvRounded sx={{ color: "#724e41", fontSize: 26 }} />
            </Box>
            <Typography sx={{ fontWeight: 800, fontSize: "16px", color: "#4b5563", mb: 0.5 }}>
              No Streams Scheduled Yet
            </Typography>
            <Typography sx={{ fontSize: "13px", color: "text.secondary", maxWidth: 300, mb: 2.5 }}>
              There are currently no live streams added to this scheduling record block.
            </Typography>
            <Button
              variant="contained"
              onClick={handleAddStreamNavigation}
              sx={{
                bgcolor: "#724e41",
                color: "white",
                borderRadius: "10px",
                textTransform: "none",
                fontWeight: 700,
                px: 3,
                py: 1,
                fontSize: "13px",
                boxShadow: 0,
                "&:hover": { bgcolor: "#5d3e33", boxShadow: 0 },
              }}
            >
              Schedule First Stream
            </Button>
          </Box>
        )}
      </Paper>
    </Paper>
  );
}